import { Component, h } from 'preact'
import EmptyPage from '../empty-page/EmptyPage'
import globals from '../../utils/globals'
import ButtonControl from '../button-control'
import { EditModal, DeleteModal } from './ProductAreaModals'
import Header from '../header'
import Loader from '../ui-components/Loader'
import Table from '../table/Table'
import axios from 'axios'
import AlertComponent from '../ui-components/AlertComponent'

interface ProductAreaType {
  id: string,
  name: string,
  productUpdateCount: number
}

interface Props {
  productAreas: Array<ProductAreaType>
}

interface State {
  productAreas: Array<ProductAreaType>,
  productUpdateToEdit: null | ProductAreaType,
  productUpdateToDelete: null | ProductAreaType,
  showCreateModal: boolean,
  showEditModal: boolean,
  showDeleteModal: boolean,
  messageType?: string|null,
  message?: string,
  modalErrorMessage?: string,
  isLoading: boolean
}

class ProductAreas extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      productAreas: props.productAreas,
      productUpdateToEdit: null,
      productUpdateToDelete: null,
      showCreateModal: false,
      showEditModal: false,
      showDeleteModal: false,
      isLoading: false
    }

    this.createProductArea = this.createProductArea.bind(this)
    this.onClickEditModalInput = this.onClickEditModalInput.bind(this)
    this.deleteProductArea = this.deleteProductArea.bind(this)
  }

  async componentDidMount () {
    const { data } = await axios.get('/community/api/productAreas')
    if (data) {
      this.setState({ productAreas: data.result })
    }
  }

  async createProductArea (newProductArea: string) {
    if (!newProductArea) {
      this.setState({ modalErrorMessage: 'Please enter product area name' })
      return
    }
    const successMessage = `New product area '${newProductArea}' is added successfully`
    this.setState({ messageType: null, isLoading: true })

    try {
      const res = await axios.post(`/community/api/productAreas/create`, {
        name: newProductArea
      })
      const newList: Array<ProductAreaType> = this.state.productAreas.concat([{ name: newProductArea, id: res.headers.id || null, productUpdateCount: 0 }])
      this.setState({ isLoading: false, message: successMessage, messageType: 'success', showCreateModal: false, productAreas: newList })
    } catch (err) {
      let errorMessage = err.response?.data?.description || 'Something went wrong, please try again.'
      this.setState({ modalErrorMessage: errorMessage, isLoading: false })
    }
  }

  async updatedProductAreaName (updatedProductArea: string, id?: string) {
    if (!updatedProductArea) {
      this.setState({ modalErrorMessage: 'Please enter product area name' })
      return
    }
    this.setState({ messageType: null, isLoading: true })

    try {
      await axios.post(`/community/api/productAreas/rename`, {
        id: id,
        name: updatedProductArea
      })
      this.setState({ messageType: null, isLoading: true })
      const successMessage = `Product area '${this.state.productUpdateToEdit?.name}' has been renamed to '${updatedProductArea}'!`
      const newProductAreaList = this.state.productAreas.map(pa => pa.id === id ? {...pa, name: updatedProductArea} : pa)

      this.setState({
        isLoading: false,
        showEditModal: false,
        message: successMessage,
        messageType: 'success',
        productUpdateToEdit: null,
        productAreas: newProductAreaList
      })
    } catch (err) {
      let errorMessage = err.response?.data?.description || 'Something went wrong, please try again.'
      this.setState({ modalErrorMessage: errorMessage, isLoading: false })
    }
  }

  async deleteProductArea (id: string) {
    const productAreaToDelete = this.state.productUpdateToDelete
    this.setState({ messageType: null, isLoading: true })
    const successMessage = `Product area '${productAreaToDelete?.name}' has been deleted.`

    try {
      await axios.post(`/community/api/productAreas/delete`, {
        id: id
      })
      const newProductAreaList = this.state.productAreas.filter(pa => pa.id !== id)

      this.setState({
        isLoading: false,
        showEditModal: false,
        message: successMessage,
        messageType: 'success',
        productUpdateToDelete: null,
        productAreas: newProductAreaList
      })
    } catch (err) {
      let errorMessage = err.response?.data?.errors || 'Something went wrong, please try again.'
      this.setState({ modalErrorMessage: errorMessage, isLoading: false })
    }
  }

  onClickEditModalInput () {
    if (this.state.modalErrorMessage) {
      this.setState({ modalErrorMessage: undefined })
    }
  }

  render (_: Props, { productAreas, productUpdateToEdit, productUpdateToDelete, showCreateModal, showEditModal, showDeleteModal, modalErrorMessage, isLoading, messageType, message }: State) {
    const model = [{
      header: 'Product areas',
      accessor: 'title'
    },
    {
      header: 'Actions',
      accessor: 'actions'
    }]
    const rows = productAreas.map((productArea: ProductAreaType) => (
      {
        title: productArea.name,
        usedBy: `${productArea.productUpdateCount} product updates`,
        actions: <div>
          <ButtonControl
            class='qa-edit-product-area'
            newStyle
            variant='secondary'
            title='Edit'
            onClick={() => this.setState({ showEditModal: true, productUpdateToEdit: productArea })}
          />
          <ButtonControl
            class='qa-edit-product-area'
            variant='warning'
            newStyle
            title='Delete'
            onClick={() => this.setState({ showDeleteModal: true, productUpdateToDelete: productArea })}
          />
        </div>
      }
    ))

    return (
      <div className='product-areas-wrapper'>
        <Header
          title='Product Areas'
          disableCancelButton
          disableSaveButton
          customButtons={productAreas.length ? (
            [
              <ButtonControl
                class='qa-add-product-area'
                variant='brand'
                newStyle
                title='Add product area'
                onClick={() => this.setState({ showCreateModal: true })}
              />]
          ) : []}
        />
        { this.state.messageType ? <AlertComponent messageType={this.state.messageType} message={message} sticky isVisibleFor={3000} /> : null }
        {isLoading && <Loader />}
        <div className='alert alert-info'>
          <strong>What is product area?</strong>
          <p>Product areas can be added as metadata to product updates and ideas. Users can filter on product areas to see all relevant content within a specific product area.</p>
        </div>
        {
          productAreas.length ? (
            <div>
              <Table model={model} rows={rows} />
            </div>
          ) : (
            <EmptyPage
              img_src={`${globals('defaultCdn')}/images/empty_page.png`}
              header='No product areas found'
              text={`There's nothing here. Get started by adding your first product area.`}
              children={<ButtonControl title='Add product area'
                class='qa-add-product-area control_empty--btn'
                variant='primary' onClick={() => this.setState({ showCreateModal: true })} />}
            />
          )
        }
        {showCreateModal ? (
          <EditModal
            onClose={() => this.setState({ showCreateModal: false, modalErrorMessage: undefined })}
            onClick={this.onClickEditModalInput}
            onSave={(newProductArea: string) => this.createProductArea(newProductArea)}
            error={modalErrorMessage} />
        ) : null}
        {showEditModal && productUpdateToEdit ? (
          <EditModal
            productArea={productUpdateToEdit.name}
            id={productUpdateToEdit.id}
            onClose={() => this.setState({ productUpdateToEdit: null, modalErrorMessage: undefined })}
            onClick={this.onClickEditModalInput}
            onSave={(updatedName: string, id?: string) => this.updatedProductAreaName(updatedName, id)}
            error={modalErrorMessage}
          />
        ) : null}
        {showDeleteModal && productUpdateToDelete ? (
          <DeleteModal
            productArea={productUpdateToDelete.name}
            id={productUpdateToDelete.id}
            onClose={() => this.setState({ productUpdateToDelete: null })}
            onDelete={(id: string) => this.deleteProductArea(id)}
          />
        ) : null}
      </div>
    )
  }
}

export default ProductAreas
